import React, { useContext, MouseEvent, FC, memo, ReactNode } from 'react';
import { useMutation } from '@apollo/client';
import { Icon } from '@blueprint/ui';
import { parse, stringify } from 'querystring';
import { useSelector } from 'react-redux';

import { filtersValuesSelector } from '../../../reducers/filters';
import { polyencSelector } from '../../../reducers/searchResults';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import { SearchContext } from '../../SearchContext';
import { constructEditSavedSearchFields } from '../../helpers';
import {
    StyledBanner,
    BannerLink,
    BannerLinkContainer,
    CloseButtonStyle
} from './EditSavedSearchBanner.style';
import {
    EDIT_SAVED_SEARCH,
    CREATE_SAVED_SEARCH
} from '../../../queries/search/editSavedSearch';
import searchTranslations from 'public/locales/en/search.json';

type Props = {
    onBannerClose?: () => void;
};

const t = getTranslator(searchTranslations);

export const EditSavedSearchBanner: FC<Props> = ({ onBannerClose }) => {
    const {
        state: {
            userAlertId,
            savedSearchAndAlerts,
            isEditSavedSearchSuccess,
            searchType
        },
        dispatch
    } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);
    const polyenc = useSelector(polyencSelector);

    const onMutationError = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: false
        });
    };

    const [editSavedSearchMutation] = useMutation(EDIT_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.updateSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.createNewSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const pathAndQuery = (savedSearchAndAlerts?.uri || '').split('?');
    const query = parse(pathAndQuery[1]);
    delete query.category;
    query.pref = 'savedsearchres';
    const editSavedSearchUrl = `${pathAndQuery[0]}?${stringify(query)}`;

    const onClose = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: false
        });
        if (onBannerClose) {
            onBannerClose();
        }
    };

    const onUpdateSavedSearchClick = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            polyenc
        });

        editSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    const onSaveAsNewSearchClicked = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        createSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    if (isEditSavedSearchSuccess === null) {
        return (
            <MemoizedStyledBanner
                strapline={t('actionBar.banners.ctas.strapline')}
                title={t('actionBar.banners.ctas.title')}
                text={t('actionBar.banners.ctas.text')}
            >
                <BannerLinkContainer>
                    <BannerLink
                        variant="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        {t('actionBar.optionsList.update')}
                    </BannerLink>
                    <BannerLink
                        variant="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                        onClick={onSaveAsNewSearchClicked}
                    >
                        {t('actionBar.optionsList.saveAsNewSearch')}
                    </BannerLink>
                    <CloseButton onClick={onClose} />
                </BannerLinkContainer>
            </MemoizedStyledBanner>
        );
    } else if (isEditSavedSearchSuccess === true) {
        return (
            <MemoizedStyledBanner
                strapline={t('actionBar.banners.success.strapline')}
                title={t('actionBar.banners.success.title')}
                text=""
                type="success"
            >
                <CloseButton onClick={onClose} />
            </MemoizedStyledBanner>
        );
    } else if (isEditSavedSearchSuccess === false) {
        return (
            <MemoizedStyledBanner
                strapline={t('actionBar.banners.failure.strapline')}
                title={t('actionBar.banners.failure.title')}
                text={t('actionBar.banners.failure.text')}
                type="failure"
            >
                <BannerLinkContainer>
                    <BannerLink
                        variant="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        {t('actionBar.optionsList.update')}
                    </BannerLink>
                    <BannerLink
                        variant="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                    >
                        {t('actionBar.optionsList.saveAsNewSearch')}
                    </BannerLink>
                    <CloseButton onClick={onClose} />
                </BannerLinkContainer>
            </MemoizedStyledBanner>
        );
    }
    return null;
};

type BannerProps = {
    strapline: string;
    title: string;
    text?: string;
    type?: 'alert' | 'success' | 'failure';
    children: ReactNode;
};

const CloseButton: FC<{ onClick: () => void }> = ({ onClick }) => (
    <CloseButtonStyle
        type="button"
        onClick={onClick}
        aria-label="close"
        data-testid="edit-saved-search-banner-close-button"
    >
        <Icon iconId="close" size="small" />
    </CloseButtonStyle>
);

const MemoizedStyledBanner = memo<BannerProps>(
    ({ strapline, title, text = '', type = 'alert', children }) => {
        return (
            <StyledBanner
                strapline={strapline}
                straplineColor="text-title"
                title={title}
                smallTitle
                text={text}
                iconSize="large"
                type={type}
            >
                {children}
            </StyledBanner>
        );
    }
);
MemoizedStyledBanner.displayName = 'MemoizedStyledBanner';
